import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  getProgressReportWeeksApi,
  previewProgressReportApi,
  sendProgressReportApi,
} from "src/DAL/tracker/progressReport";
import { schedulePointsOf } from "./automationBuilder/triggerConfig";

const MUTED = "#64748b";
const INK = "#0f172a";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const BORDER = "#e2e8f0";
const WARN = "#b45309";
const WARN_SOFT = "#fdf1dc";
const WARN_BORDER = "#f3d19c";

/** Report tone from the backend (real results, never demo values). */
const MOOD_META = {
  mixed: { label: "Mixed results", color: WARN, bg: WARN_SOFT },
  support: { label: "Declining", color: WARN, bg: WARN_SOFT },
};
const VERDICT_CHIP = {
  down: { label: "Declining", color: WARN, bg: WARN_SOFT },
  mixed: { label: "Mixed", color: WARN, bg: WARN_SOFT },
};

const trendMoodOf = (row) => row?.trend?.mood || row?.mood || "";
const isWarnMood = (mood) => mood === "mixed" || mood === "support";

const declinedText = (row) =>
  (row?.trend?.declined || [])
    .map((m) => `${m.label} ${m.firstText} → ${m.latestText} (${m.changeText})`)
    .join(" · ");

const weekChipSx = (selected) => ({
  fontWeight: selected ? 700 : 500,
  bgcolor: selected
    ? "color-mix(in srgb, var(--portal-theme-primary) 16%, white)"
    : "#f1f5f9",
  color: selected ? PRIMARY : MUTED,
  border: selected ? `1px solid ${PRIMARY}` : `1px solid ${BORDER}`,
});

const openPdfPreview = (pdf) => {
  if (!pdf?.base64) return;
  const byteChars = atob(pdf.base64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: pdf.contentType || "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * Coach free-hand progress report: pick members + weeks → Generate
 * (preview) → Send. Results that improved are celebrated; when results went
 * the wrong way the coach is warned before Send and the report gives
 * friendly next steps instead of a celebration.
 */
export default function CoachProgressReportDialog({
  open,
  tracker,
  people = [],
  initialMembers = [],
  onClose,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const trackerId = tracker?.id || tracker?._id;
  const localSchedulePoints = useMemo(
    () => schedulePointsOf(tracker, null),
    [tracker]
  );

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [sendOnPointKey, setSendOnPointKey] = useState("");
  const [compareFromPointKeys, setCompareFromPointKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [apiWeeks, setApiWeeks] = useState([]);
  const [weeksLoading, setWeeksLoading] = useState(false);
  // Second click on Send when some results went the wrong way.
  const [confirmSend, setConfirmSend] = useState(false);

  const schedulePoints = apiWeeks.length ? apiWeeks : localSchedulePoints;

  const activePeople = useMemo(
    () =>
      (people || []).filter(
        (p) => !p.left && !p.dropped && (p.memberId || p.personKey || p.email)
      ),
    [people]
  );

  const personKeyOf = (p) =>
    String(p.key || p.personKey || p.memberId || p.email || "");

  useEffect(() => {
    if (!open) return;
    const initial = (initialMembers || [])
      .map(personKeyOf)
      .filter(Boolean);
    if (initial.length) {
      setSelectedKeys(initial);
    } else {
      setSelectedKeys([]);
    }
    setSendOnPointKey("");
    setCompareFromPointKeys([]);
    setPreviews([]);
    setBusy(false);
  }, [open, initialMembers]);

  // Any change to the previews asks for the warning again.
  useEffect(() => {
    setConfirmSend(false);
  }, [previews]);

  useEffect(() => {
    if (!open || !trackerId) {
      setApiWeeks([]);
      return undefined;
    }
    let cancelled = false;
    setWeeksLoading(true);
    (async () => {
      try {
        const res = await getProgressReportWeeksApi(trackerId);
        if (cancelled) return;
        const weeks = Array.isArray(res?.weeks)
          ? res.weeks
              .map((point) => ({
                key: String(point.key || ""),
                label: point.label || point.key,
                amount: Number(point.amount) || 0,
                unit: String(point.unit || "weeks"),
              }))
              .filter((point) => point.key)
          : [];
        setApiWeeks(weeks);
      } catch (_err) {
        if (!cancelled) setApiWeeks([]);
      } finally {
        if (!cancelled) setWeeksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, trackerId]);

  const weeksBeforeSend = useMemo(() => {
    const idx = schedulePoints.findIndex((p) => p.key === sendOnPointKey);
    if (idx <= 0) return [];
    return schedulePoints.slice(0, idx);
  }, [schedulePoints, sendOnPointKey]);

  /** First schedule week can stand alone; later weeks need a compare week. */
  const compareRequired = Boolean(sendOnPointKey && weeksBeforeSend.length);

  const toggleMember = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
    setPreviews([]);
  };

  const selectAll = () => {
    setSelectedKeys(activePeople.map(personKeyOf));
    setPreviews([]);
  };

  const setSendOn = (key) => {
    const next = sendOnPointKey === key ? "" : key;
    setSendOnPointKey(next);
    const idx = schedulePoints.findIndex((p) => p.key === next);
    const before = new Set(
      (idx > 0 ? schedulePoints.slice(0, idx) : []).map((p) => p.key)
    );
    setCompareFromPointKeys((prev) => prev.filter((k) => before.has(k)));
    setPreviews([]);
  };

  const toggleCompare = (key) => {
    setCompareFromPointKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
    setPreviews([]);
  };

  const selectedMembersPayload = () =>
    activePeople
      .filter((p) => selectedKeys.includes(personKeyOf(p)))
      .map((p) => ({
        memberId: p.memberId || "",
        personKey: p.personKey || p.key || "",
        email: p.email || "",
        name: p.name || p.memberName || "",
      }));

  const handleGenerate = async () => {
    const members = selectedMembersPayload();
    if (!members.length) {
      enqueueSnackbar("Pick at least one member", { variant: "warning" });
      return;
    }
    if (!sendOnPointKey) {
      enqueueSnackbar("Pick which week to report on", { variant: "warning" });
      return;
    }
    if (compareRequired && !compareFromPointKeys.length) {
      enqueueSnackbar("Pick at least one earlier week to compare from", {
        variant: "warning",
      });
      return;
    }
    setBusy(true);
    try {
      const res = await previewProgressReportApi({
        trackerId,
        members,
        sendOnPointKey,
        compareFromPointKeys,
      });
      if (res?.code !== 200) {
        enqueueSnackbar(res?.message || "Could not generate report", {
          variant: "error",
        });
        return;
      }
      setPreviews(Array.isArray(res.results) ? res.results : []);
      const ok = (res.results || []).filter((r) => r.ok).length;
      enqueueSnackbar(res.message || `Generated ${ok} report(s)`, {
        variant: ok ? "success" : "warning",
      });
    } catch (_err) {
      enqueueSnackbar("Could not generate report", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    const okPreviews = previews.filter((r) => r.ok && r.pdf);
    if (!okPreviews.length) {
      enqueueSnackbar("Generate a report first", { variant: "warning" });
      return;
    }
    // Results went the wrong way: warn first, send on the second click.
    if (okPreviews.some((r) => isWarnMood(trendMoodOf(r))) && !confirmSend) {
      setConfirmSend(true);
      return;
    }
    setBusy(true);
    try {
      const res = await sendProgressReportApi({
        trackerId,
        members: okPreviews.map((r) => r.member),
        sendOnPointKey,
        compareFromPointKeys,
      });
      if (res?.code !== 200) {
        enqueueSnackbar(res?.message || "Could not send report", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(res.message || "Reports sent", { variant: "success" });
      onClose?.();
    } catch (_err) {
      enqueueSnackbar("Could not send report", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const okCount = previews.filter((r) => r.ok).length;
  const warnPreviews = previews.filter((r) => isWarnMood(trendMoodOf(r)));
  const warnReady = warnPreviews.filter((r) => r.ok && r.pdf);
  // Demo flag: only people without real values get sample numbers.
  const demoRows = previews.filter((r) => r.ok && r.demoValues);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: INK, pb: 1 }}>
        Progress report
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
          Pick members and weeks, generate to preview, then send. Results
          that improved are celebrated; if results went the wrong way, the
          report gives friendly next steps instead. Works for one person or
          many on this journey.
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>
            Members
          </Typography>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={selectAll}
            style={{ padding: "4px 10px" }}
            disabled={busy}
          >
            Select all
          </button>
        </Box>
        <Box
          sx={{
            maxHeight: 180,
            overflowY: "auto",
            border: `1px solid ${BORDER}`,
            borderRadius: "10px",
            p: 1,
            mb: 2,
            bgcolor: "#fafbfc",
          }}
        >
          {activePeople.length ? (
            activePeople.map((p) => {
              const key = personKeyOf(p);
              const verdictChip = VERDICT_CHIP[p.performance?.verdict];
              return (
                <FormControlLabel
                  key={key}
                  sx={{ display: "flex", m: 0, py: 0.25 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedKeys.includes(key)}
                      onChange={() => toggleMember(key)}
                      disabled={busy}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13, color: INK }}>
                      {p.name || p.email || "Member"}
                      {p.email ? (
                        <Typography
                          component="span"
                          sx={{ fontSize: 12, color: MUTED, ml: 0.75 }}
                        >
                          {p.email}
                        </Typography>
                      ) : null}
                      {verdictChip ? (
                        <Chip
                          size="small"
                          label={verdictChip.label}
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            color: verdictChip.color,
                            bgcolor: verdictChip.bg,
                          }}
                        />
                      ) : null}
                    </Typography>
                  }
                />
              );
            })
          ) : (
            <Typography sx={{ fontSize: 13, color: MUTED, p: 1 }}>
              No active members on this journey.
            </Typography>
          )}
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK, mb: 0.5 }}>
          Send report on
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>
          Any schedule week — including past check-ins.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
          {weeksLoading ? (
            <CircularProgress size={18} sx={{ color: PRIMARY, m: 0.5 }} />
          ) : null}
          {schedulePoints.map((point) => (
            <Chip
              key={point.key}
              size="small"
              label={point.label || point.key}
              onClick={() => !busy && setSendOn(point.key)}
              sx={weekChipSx(sendOnPointKey === point.key)}
            />
          ))}
          {!weeksLoading && !schedulePoints.length ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              This journey has no schedule weeks yet.
            </Typography>
          ) : null}
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK, mb: 0.5 }}>
          Compare from
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>
          {!sendOnPointKey
            ? "Pick a send week first."
            : compareRequired
              ? "Earlier week(s) to compare against (required)."
              : "Not needed for the first week — this will be a single-week report."}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
          {weeksBeforeSend.map((point) => (
            <Chip
              key={point.key}
              size="small"
              label={point.label || point.key}
              onClick={() => !busy && toggleCompare(point.key)}
              sx={weekChipSx(compareFromPointKeys.includes(point.key))}
            />
          ))}
          {sendOnPointKey && !weeksBeforeSend.length ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              No earlier weeks before the send week.
            </Typography>
          ) : null}
        </Box>

        {previews.length ? (
          <Box
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: "10px",
              p: 1.5,
              bgcolor: "#fff",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK, mb: 1 }}>
              Preview ({okCount} ready)
            </Typography>
            {demoRows.length ? (
              <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>
                Demo values are on: members with no results to compare yet
                get sample numbers in their PDF (marked "Sample values").
                Members with real results get their own numbers.
              </Typography>
            ) : null}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {previews.map((row, i) => {
                const moodMeta = MOOD_META[trendMoodOf(row)];
                const declined = declinedText(row);
                return (
                <Box
                  key={`${row.member?.email || i}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: "8px",
                    bgcolor: "#fafbfc",
                    border: `1px solid ${moodMeta ? WARN_BORDER : BORDER}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>
                      {row.member?.name || row.member?.email || "Member"}
                      {moodMeta ? (
                        <Chip
                          size="small"
                          label={moodMeta.label}
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            color: moodMeta.color,
                            bgcolor: moodMeta.bg,
                          }}
                        />
                      ) : null}
                      {row.ok && row.demoValues ? (
                        <Chip
                          size="small"
                          label="Sample values"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            color: MUTED,
                            bgcolor: "#f1f5f9",
                          }}
                        />
                      ) : null}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                      {row.ok
                        ? row.rangeLabel || "Ready"
                        : row.detail || "Failed"}
                    </Typography>
                    {declined ? (
                      <Typography sx={{ fontSize: 12, color: WARN, mt: 0.25 }}>
                        Went the wrong way: {declined}
                      </Typography>
                    ) : null}
                  </Box>
                  {row.ok && row.pdf ? (
                    <button
                      type="button"
                      className="small-contained-outlined-button"
                      style={{ padding: "6px 12px" }}
                      onClick={() => openPdfPreview(row.pdf)}
                      disabled={busy}
                    >
                      View PDF
                    </button>
                  ) : null}
                </Box>
                );
              })}
            </Box>
            {warnPreviews.length ? (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: "8px",
                  bgcolor: WARN_SOFT,
                  border: `1px solid ${WARN_BORDER}`,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: WARN }}>
                  {warnPreviews.length === 1
                    ? "1 member's results went the wrong way"
                    : `${warnPreviews.length} members' results went the wrong way`}
                </Typography>
                <Typography sx={{ fontSize: 12, color: INK, mt: 0.25 }}>
                  Their report won't celebrate those results: it shows them
                  honestly with friendly next steps. Open the PDF and check it
                  before you send.
                </Typography>
                {confirmSend && warnReady.length ? (
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: WARN, mt: 0.5 }}>
                    Press "Send anyway" to send all {okCount} report(s).
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={onClose}
          disabled={busy}
          style={{ padding: "8px 16px" }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={handleGenerate}
          disabled={busy}
          style={{ padding: "8px 16px", opacity: busy ? 0.6 : 1 }}
        >
          {busy && !okCount ? (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          ) : null}
          Generate
        </button>
        <button
          type="button"
          className="small-contained-button"
          onClick={handleSend}
          disabled={busy || !okCount}
          style={{ padding: "8px 16px", opacity: busy || !okCount ? 0.6 : 1 }}
        >
          {confirmSend ? "Send anyway" : "Send"} {okCount ? `(${okCount})` : ""}
        </button>
      </DialogActions>
    </Dialog>
  );
}
